// Ref: https://github.com/johnpolacek/date-range-picker-for-shadcn/tree/main

import { CalendarIcon, CheckIcon } from 'lucide-react';
import {
  type FC,
  type ReactElement,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { DateRange as CalendarDateRange } from 'react-day-picker';

import { Button } from '@/components/shadcn/ui/button';
import { Calendar } from '@/components/shadcn/ui/calendar';
import { DateInput } from '@/components/shadcn/ui/date-input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shadcn/ui/popover';
import { cn } from '@/lib/cn';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

import {
  clampDateRangePickerValueToBounds,
  clampDayjsToDateRangeBounds,
  getAllowedDateRangePickerPresetNames,
  isSameDateRangePickerValue,
} from './date-range-picker.utils';
import {
  getPresetDateRange,
  type PresetName,
  PRESETS,
} from './get-preset-date-range';
import type { DateRange } from './types';

export type DateRangePickerValue =
  | {
      type: 'dateRange';
      dateRange: DateRange;
    }
  | {
      type: 'preset';
      presetName: PresetName;
    };

export interface DateRangePickerProps {
  /** Current date range value */
  value?: DateRangePickerValue;
  /** Callback when date range changes */
  onChange?: (value: DateRangePickerValue) => void;
  /** Control popover open state */
  open?: boolean;
  /** Callback when popover open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Placeholder text when no date is selected */
  placeholder?: string;
  /** Custom trigger element rendered as child */
  trigger?: ReactElement;
  /** Smallest selectable date */
  minDate?: DateRange['from'];
  /** Largest selectable date */
  maxDate?: DateRange['to'];
  /** Allowed preset names */
  presetNames?: PresetName[];
  /** Whether to show preset shortcuts */
  showPresets?: boolean;
  /** Maximum inclusive number of calendar days allowed */
  maxRangeDays?: number;
}

const createDefaultRange = (): DateRangePickerValue => {
  const today = getTimezoneDayjs().startOf('day').tz();
  const thirtyDaysAgo = today.clone().tz().subtractVal(30, 'day');

  return { type: 'dateRange', dateRange: { from: thirtyDaysAgo, to: today } };
};

const getDisplayText = (
  value: DateRangePickerValue | undefined,
  placeholder: string,
) => {
  if (!value) return placeholder;

  if (value.type === 'preset') {
    const preset = PRESETS.find((item) => item.name === value.presetName);
    return preset?.label || 'Custom';
  }

  const startDate = getTimezoneDayjs(value.dateRange.from.toJSON()).format(
    DATE_FORMAT.readable,
  );
  const endDate = getTimezoneDayjs(value.dateRange.to.toJSON()).format(
    DATE_FORMAT.readable,
  );

  return `${startDate} - ${endDate}`;
};

const getComputedDateRange = (value: DateRangePickerValue): DateRange => {
  if (value.type === 'preset') {
    return getPresetDateRange(value.presetName);
  }

  return value.dateRange;
};

export const getCalendarDateKey = (date: Date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');

export type DateRangePickerPanelPresetOption = {
  value: string;
  label: string;
};

export type DateRangePickerPanelProps = {
  computedDateRange: DateRange;
  isSmallScreen: boolean;
  maxDate?: DateRange['to'];
  minDate?: DateRange['from'];
  onCalendarSelect: (value: CalendarDateRange | undefined) => void;
  onCancel: () => void;
  onEndDateChange: (date: DateRange['to']) => void;
  onPresetSelect: (preset: string) => void;
  onStartDateChange: (date: DateRange['from']) => void;
  onUpdate: () => void;
  presetOptions: DateRangePickerPanelPresetOption[];
  selectedPresetValue?: string;
  showPresets: boolean;
  validationMessage?: string;
};

export const DateRangePickerPanel = ({
  computedDateRange,
  isSmallScreen,
  maxDate,
  minDate,
  onCalendarSelect,
  onCancel,
  onEndDateChange,
  onPresetSelect,
  onStartDateChange,
  onUpdate,
  presetOptions,
  selectedPresetValue,
  showPresets,
  validationMessage,
}: DateRangePickerPanelProps) => (
  <div className="flex flex-col">
    <div className="border-b px-4 py-4">
      <div className="flex flex-row items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <DateInput
              value={computedDateRange.from}
              onChange={onStartDateChange}
            />
            <span className="py-1 text-muted-foreground">-</span>
            <DateInput
              value={computedDateRange.to}
              onChange={onEndDateChange}
            />
          </div>
          {validationMessage ? (
            <p role="alert" className="text-xs text-destructive">
              {validationMessage}
            </p>
          ) : null}
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={onUpdate}
            disabled={validationMessage != null}
          >
            Update
          </Button>
        </div>
      </div>
    </div>
    <div className="flex gap-4 p-4 h-95">
      <Calendar
        key={computedDateRange.from.format('YYYY-MM-DD')}
        mode="range"
        onSelect={onCalendarSelect}
        selected={{
          from: computedDateRange.from.toDate(),
          to: computedDateRange.to.toDate(),
        }}
        disabled={(date) => {
          if (minDate && date < minDate.toDate()) {
            return true;
          }

          if (maxDate && date > maxDate.toDate()) {
            return true;
          }

          return false;
        }}
        numberOfMonths={isSmallScreen ? 1 : 2}
        defaultMonth={computedDateRange.from.toDate()}
      />
      {showPresets && (
        <div className="min-w-40 space-y-1 flex flex-col gap-1">
          {presetOptions.map((preset) => (
            <PresetButton
              key={preset.value}
              onClick={() => {
                onPresetSelect(preset.value);
              }}
              label={preset.label}
              isSelected={selectedPresetValue === preset.value}
            />
          ))}
        </div>
      )}
    </div>
  </div>
);

/** The DateRangePicker component allows a user to select a range of dates */
export const DateRangePicker: FC<DateRangePickerProps> = ({
  value,
  onChange,
  open: controlledOpen,
  onOpenChange,
  placeholder = 'Pick a date range',
  trigger,
  minDate,
  maxDate,
  presetNames,
  showPresets = true,
  maxRangeDays,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen ?? internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;

  const defaultRange = useMemo(createDefaultRange, []);
  const availablePresetNames = useMemo(() => {
    const boundedPresetNames = getAllowedDateRangePickerPresetNames({
      minDate,
      maxDate,
    });

    return presetNames
      ? presetNames.filter((name) => boundedPresetNames.includes(name))
      : boundedPresetNames;
  }, [maxDate, minDate, presetNames]);
  const normalizedValue = useMemo(
    () =>
      clampDateRangePickerValueToBounds(value ?? defaultRange, {
        minDate,
        maxDate,
        allowedPresetNames: availablePresetNames,
      }),
    [availablePresetNames, defaultRange, maxDate, minDate, value],
  );
  const [tempValue, setTempValue] =
    useState<DateRangePickerValue>(normalizedValue);
  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 960 : false,
  );

  useEffect(() => {
    const handleResize = (): void => {
      setIsSmallScreen(window.innerWidth < 960);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setTempValue(normalizedValue);
  }, [isOpen, normalizedValue]);

  useEffect(() => {
    setTempValue((currentValue) =>
      isSameDateRangePickerValue(currentValue, normalizedValue)
        ? currentValue
        : normalizedValue,
    );
  }, [normalizedValue]);

  const setDateRange = (newRange: DateRange): void => {
    setTempValue(
      clampDateRangePickerValueToBounds(
        { type: 'dateRange', dateRange: newRange },
        {
          minDate,
          maxDate,
          allowedPresetNames: availablePresetNames,
        },
      ),
    );
  };

  const setPreset = (preset: PresetName): void => {
    if (!availablePresetNames.includes(preset)) {
      return;
    }

    setTempValue({
      type: 'preset',
      presetName: preset,
    });
  };

  const handleStartDateChange = (date: DateRange['from']) => {
    const currentRange = getComputedDateRange(tempValue);
    const nextStartDate = clampDayjsToDateRangeBounds(date, {
      minDate,
      maxDate,
    });
    const toDate =
      nextStartDate > currentRange.to ? nextStartDate : currentRange.to;

    setDateRange({
      from: nextStartDate,
      to: toDate,
    });
  };

  const handleEndDateChange = (date: DateRange['to']) => {
    const currentRange = getComputedDateRange(tempValue);
    const nextEndDate = clampDayjsToDateRangeBounds(date, {
      minDate,
      maxDate,
    });
    const fromDate =
      nextEndDate < currentRange.from ? nextEndDate : currentRange.from;

    setDateRange({
      from: fromDate,
      to: nextEndDate,
    });
  };

  const handleCalendarSelect = (
    selectedRange: CalendarDateRange | undefined,
  ) => {
    if (selectedRange?.from == null) {
      return;
    }

    setDateRange({
      from: clampDayjsToDateRangeBounds(
        getTimezoneDayjs(getCalendarDateKey(selectedRange.from)),
        {
          minDate,
          maxDate,
        },
      ),
      to: clampDayjsToDateRangeBounds(
        getTimezoneDayjs(
          getCalendarDateKey(selectedRange.to ?? selectedRange.from),
        ),
        {
          minDate,
          maxDate,
        },
      ),
    });
  };

  const handleCancel = () => {
    setTempValue(normalizedValue);
    setIsOpen(false);
  };

  const handleUpdate = () => {
    const nextValue = clampDateRangePickerValueToBounds(tempValue, {
      minDate,
      maxDate,
      allowedPresetNames: availablePresetNames,
    });

    onChange?.(nextValue);
    setIsOpen(false);
  };

  const computedDateRange = useMemo(
    () => getComputedDateRange(tempValue),
    [tempValue],
  );
  const validationMessage = useMemo(() => {
    if (maxRangeDays == null) return undefined;
    const rangeDays =
      computedDateRange.to
        .startOf('day')
        .diff(computedDateRange.from.startOf('day'), 'day') + 1;
    return rangeDays > maxRangeDays
      ? `Select a range of ${maxRangeDays} days or fewer.`
      : undefined;
  }, [computedDateRange, maxRangeDays]);
  const buttonDisplayText = useMemo(
    () => getDisplayText(value ? normalizedValue : undefined, placeholder),
    [normalizedValue, placeholder, value],
  );

  const defaultTrigger = (
    <span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 rounded-full px-3 text-[13px] font-medium shadow-none"
      >
        <CalendarIcon className="size-3.5" />
        <span className="text-[13px] font-medium">{buttonDisplayText}</span>
      </Button>
    </span>
  );

  return (
    <div className="flex items-center gap-3">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>{trigger ?? defaultTrigger}</PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={8}
          className="w-max max-w-[calc(100vw-1rem)] p-0"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <DateRangePickerPanel
            computedDateRange={computedDateRange}
            isSmallScreen={isSmallScreen}
            minDate={minDate}
            maxDate={maxDate}
            onCalendarSelect={handleCalendarSelect}
            onCancel={handleCancel}
            onEndDateChange={handleEndDateChange}
            onPresetSelect={(preset) => {
              const matchedPreset = availablePresetNames.find(
                (presetName) => presetName === preset,
              );
              if (matchedPreset) setPreset(matchedPreset);
            }}
            onStartDateChange={handleStartDateChange}
            onUpdate={handleUpdate}
            presetOptions={availablePresetNames.map((presetName) => ({
              value: presetName,
              label:
                PRESETS.find(({ name }) => name === presetName)?.label ??
                presetName,
            }))}
            selectedPresetValue={
              tempValue.type === 'preset' ? tempValue.presetName : undefined
            }
            showPresets={showPresets}
            validationMessage={validationMessage}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

const PresetButton = ({
  label,
  isSelected,
  onClick,
}: {
  label: string;
  isSelected: boolean;
  onClick: () => void;
}) => (
  <Button
    type="button"
    variant={isSelected ? 'secondary' : 'ghost'}
    size="sm"
    className={cn('justify-start', isSelected && 'pointer-events-none')}
    onClick={onClick}
  >
    <CheckIcon
      className={cn(
        'size-4 opacity-0 transition-opacity',
        isSelected && 'opacity-70',
      )}
    />
    <span>{label}</span>
  </Button>
);
